import type { User } from 'firebase/auth';

import { API_BASE_URL, isAdminHost } from '../config';

const getSessionUrl = (): string => {
  // The admin host serves /api itself; the local dev server has no proxy.
  if (
    typeof window !== 'undefined'
    && isAdminHost(window.location.hostname)
    && process.env.NODE_ENV !== 'development'
  ) {
    return `${window.location.origin}/api/auth/session`;
  }
  return `${API_BASE_URL}/api/auth/session`;
};

let lastSyncedUid: string | null = null;
let lastSyncedToken: string | null = null;
const DEFAULT_ACCESS_DENIED_MESSAGE = 'This Google account does not have access to this staging environment.';

export class BackendSessionAccessDeniedError extends Error {
  constructor(message: string = DEFAULT_ACCESS_DENIED_MESSAGE) {
    super(message);
    this.name = 'BackendSessionAccessDeniedError';
  }
}

export function isBackendSessionAccessDeniedError(
  error: unknown,
): error is BackendSessionAccessDeniedError {
  return error instanceof BackendSessionAccessDeniedError;
}

/* The backend refused the session because the account is on the waitlist. */
export class AccountWaitlistedError extends BackendSessionAccessDeniedError {
  constructor(message: string) {
    super(message);
    this.name = 'AccountWaitlistedError';
  }
}

export function isAccountWaitlistedError(error: unknown): error is AccountWaitlistedError {
  return error instanceof AccountWaitlistedError;
}

async function readErrorPayload(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const payload = await response.clone().json();
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

function readErrorDetail(payload: Record<string, unknown> | null): string | null {
  const detail = payload?.detail;
  return typeof detail === 'string' && detail.trim() ? detail.trim() : null;
}

export async function syncBackendSessionWithToken(
  token: string,
  uid?: string | null,
): Promise<void> {
  const normalizedToken = String(token || '').trim();
  if (!normalizedToken) {
    throw new Error('Missing Firebase ID token');
  }

  const normalizedUid = String(uid || '').trim() || null;
  if (lastSyncedToken === normalizedToken && (!normalizedUid || lastSyncedUid === normalizedUid)) {
    return;
  }

  const response = await fetch(getSessionUrl(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${normalizedToken}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 403) {
      const payload = await readErrorPayload(response);
      const detail = readErrorDetail(payload);
      if (payload?.code === 'account_waitlisted') {
        throw new AccountWaitlistedError(detail || DEFAULT_ACCESS_DENIED_MESSAGE);
      }
      throw new BackendSessionAccessDeniedError(detail || DEFAULT_ACCESS_DENIED_MESSAGE);
    }
    throw new Error(`Backend session sync failed with status ${response.status}`);
  }

  lastSyncedToken = normalizedToken;
  if (normalizedUid) {
    lastSyncedUid = normalizedUid;
  }
}

export async function syncBackendSession(user: User | null): Promise<void> {
  if (!user) {
    await clearBackendSession();
    return;
  }

  const token = await user.getIdToken();
  await syncBackendSessionWithToken(token, user.uid);
}

export async function clearBackendSession(): Promise<void> {
  try {
    await fetch(getSessionUrl(), {
      method: 'DELETE',
      credentials: 'include',
    });
  } finally {
    lastSyncedUid = null;
    lastSyncedToken = null;
  }
}
